/**
 * Lightweight performance monitor for the map editor Canvas migration.
 *
 * Measures the metrics that matter for the SVG -> Canvas port:
 *   - FPS + frame time (ms)   — smoothness during pan / zoom / drag
 *   - DOM node count          — the SVG bottleneck we are removing
 *   - named measures (avg ms) — e.g. hit-test, render
 *
 * Enabled by the editor when the PERF_HUD flag is on. Also reachable from the
 * browser console as `window.__perf`:
 *   __perf.snapshot()   // current metrics
 *   __perf.reset()      // clear named measures + DOM sample
 */

interface RollingAvg {
  total: number
  count: number
}

class PerfMonitor {
  private running = false
  private rafId: number | null = null
  private target: Element | null = null

  private lastTs = 0
  private frames = 0
  private fps = 0
  private frameMs = 0
  private domNodes = 0

  private measures: Record<string, RollingAvg> = {}
  private overlay: HTMLDivElement | null = null

  start(target?: Element | null): void {
    if (typeof window === 'undefined') return
    this.target = target ?? null
    if (this.running) return
    this.running = true
    this.lastTs = performance.now()
    this.frames = 0
    this.ensureOverlay()
    this.rafId = requestAnimationFrame(this.loop)
  }

  stop(): void {
    this.running = false
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    this.removeOverlay()
  }

  setTarget(target: Element | null): void {
    this.target = target
  }

  /** Time a synchronous function and fold it into a rolling average. */
  measure<T>(label: string, fn: () => T): T {
    const t0 = performance.now()
    try {
      return fn()
    } finally {
      this.record(label, performance.now() - t0)
    }
  }

  record(label: string, ms: number): void {
    const m = this.measures[label] || (this.measures[label] = { total: 0, count: 0 })
    m.total += ms
    m.count += 1
  }

  snapshot(): {
    fps: number
    frameMs: number
    domNodes: number
    measures: Record<string, number>
  } {
    const avg: Record<string, number> = {}
    for (const [k, v] of Object.entries(this.measures)) {
      avg[k] = v.count ? +(v.total / v.count).toFixed(3) : 0
    }
    return {
      fps: Math.round(this.fps),
      frameMs: +this.frameMs.toFixed(2),
      domNodes: this.domNodes,
      measures: avg,
    }
  }

  reset(): void {
    this.measures = {}
  }

  private loop = (): void => {
    if (!this.running) return
    const now = performance.now()
    const delta = now - this.lastTs
    this.frames += 1
    // Recompute ~2x/sec so the HUD is readable and cheap.
    if (delta >= 500) {
      this.fps = (this.frames * 1000) / delta
      this.frameMs = delta / this.frames
      this.frames = 0
      this.lastTs = now
      if (this.target) {
        this.domNodes = this.target.getElementsByTagName('*').length
      }
      this.paint()
    }
    this.rafId = requestAnimationFrame(this.loop)
  }

  private ensureOverlay(): void {
    if (this.overlay || typeof document === 'undefined') return
    const el = document.createElement('div')
    el.style.cssText = [
      'position:fixed',
      'top:8px',
      'left:8px',
      'z-index:99999',
      'font:11px/1.4 monospace',
      'color:#0f0',
      'background:rgba(0,0,0,.75)',
      'padding:6px 8px',
      'border-radius:4px',
      'pointer-events:none',
      'white-space:pre',
    ].join(';')
    document.body.appendChild(el)
    this.overlay = el
  }

  private removeOverlay(): void {
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay)
    }
    this.overlay = null
  }

  private paint(): void {
    if (!this.overlay) return
    const s = this.snapshot()
    const lines = [
      `FPS ${s.fps}   frame ${s.frameMs}ms`,
      `DOM ${s.domNodes}`,
      ...Object.entries(s.measures).map(([k, v]) => `${k} ${v}ms`),
    ]
    this.overlay.textContent = lines.join('\n')
  }
}

export const perfMonitor = new PerfMonitor()

if (typeof window !== 'undefined') {
  ;(window as any).__perf = perfMonitor
}
